import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, fmtVal, docLabel } from "@/lib/api";
import { FileText, Calculator, Scale } from "lucide-react";
import { SourceBadge } from "./SourceBadge";

function Highlighted({ text, snippet }) {
  const idx = snippet ? text.toLowerCase().indexOf(snippet.toLowerCase().slice(0, 40)) : -1;
  if (idx < 0) return <>{text}</>;
  const end = idx + Math.min(snippet.length, text.length - idx);
  return (
    <>
      {text.slice(0, idx)}
      <mark data-testid="source-highlight" className="bg-sky-600/25 text-sky-900 rounded px-0.5">{text.slice(idx, end)}</mark>
      {text.slice(end)}
    </>
  );
}

function DocView({ source }) {
  const [doc, setDoc] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    setDoc(null);
    api.get(`/documents/${source.doc_id}`).then((r) => setDoc(r.data)).catch(() => setDoc({ error: true }));
  }, [source.doc_id]);
  useEffect(() => { ref.current?.scrollIntoView({ block: "start" }); }, [doc, source.page]);
  if (!doc) return <div className="text-sm text-slate-500 p-6">Loading document…</div>;
  if (doc.error) return <div className="text-sm text-red-600 p-6">Document not found.</div>;
  return (
    <div className="max-h-[60vh] overflow-y-auto thin-scroll space-y-4 pr-1">
      {doc.pages.map((p, i) => {
        const active = i + 1 === Number(source.page);
        return (
          <div key={i} ref={active ? ref : null} data-testid={`source-page-${i + 1}`}
            className={`rounded border p-4 bg-slate-50 ${active ? "border-sky-500/70 shadow-[0_0_0_3px_rgba(14,165,233,0.12)]" : "border-slate-200"}`}>
            <div className="flex justify-between eyebrow mb-3"><span>{doc.name}</span><span>Page {i + 1} / {doc.pages.length}</span></div>
            <pre className="whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-slate-700">
              {active ? <Highlighted text={p} snippet={source.snippet} /> : p}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

function CalcView({ metric, metrics, onChange }) {
  return (
    <div className="space-y-4">
      <div className="panel p-4">
        <div className="eyebrow mb-1">Result</div>
        <div className="num text-3xl text-slate-900" data-testid="calc-result-value">{fmtVal(metric.value, metric.format)}</div>
        <div className="eyebrow mt-4 mb-1">Formula (deterministic, not AI)</div>
        <code className="num text-sm text-sky-700" data-testid="calc-formula">{metric.formula}</code>
        {metric.policy && (
          <div className={`mt-3 text-xs num ${metric.policy.pass ? "text-emerald-600" : "text-red-600"}`}>
            Policy {metric.policy.op} {fmtVal(metric.policy.threshold, metric.format)} : {metric.policy.pass ? "PASS" : "BREACH"}
          </div>
        )}
      </div>
      <div className="eyebrow">Inputs</div>
      <div className="divide-y divide-slate-200 border border-slate-200 rounded">
        {metric.inputs.map((i) => (
          <div key={i.key} className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="text-slate-600">{i.label}</span>
            <span className="flex items-center gap-3">
              <span className="num text-slate-900">{fmtVal(i.value, i.format)}</span>
              {i.source?.calc ? (
                <button data-testid={`calc-input-trace-${i.key}`} onClick={() => onChange({ metric: metrics[i.source.calc], metrics })}
                  className="text-[10px] num px-1.5 py-0.5 rounded border border-violet-500/40 text-violet-700 hover:bg-violet-500/10 transition-colors">
                  calc:{i.source.calc}
                </button>
              ) : i.source ? <SourceBadge src={i.source} onOpen={onChange} /> : <span className="text-[10px] text-amber-600">missing</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RefView({ source }) {
  const [d, setD] = useState(null);
  const path = source.policy_id ? `/policies/${source.policy_id}` : `/compliance/rules/${source.rule_id}`;
  useEffect(() => { setD(null); api.get(path).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [path]);
  if (!d) return <div className="text-sm text-slate-500 p-6">Loading…</div>;
  if (d.error) return <div className="text-sm text-red-600 p-6">Not found.</div>;
  return (
    <div className="space-y-3" data-testid="reference-view">
      <div className="panel p-4">
        <div className="eyebrow mb-1">{source.policy_id ? d.version : `${d.jurisdiction} · ${d.topic}`}</div>
        <div className="font-display text-lg text-slate-900">{d.id} - {d.title}</div>
        {d.citation && <div className="num text-xs text-indigo-700 mt-1" data-testid="rule-citation">{d.citation}</div>}
        {d.effective_date && <div className="num text-[11px] text-slate-600 mt-1" data-testid="rule-effective-date">Effective {d.effective_date}</div>}
        <p className="text-sm text-slate-700 mt-3 leading-relaxed">{d.text || d.summary}</p>
      </div>
      {d.threshold != null && <div className="text-xs num text-slate-600">Limit: {d.metric} {d.op} {d.threshold} · {d.kind === "hard" ? "Hard limit" : "Condition required"} · applies to {d.classes.join(", ")}</div>}
      {d.parameters && <div className="grid grid-cols-2 gap-2">{Object.entries(d.parameters).map(([k, v]) => <div key={k} className="bg-slate-50 border border-slate-200 rounded px-3 py-2"><div className="text-[10px] text-slate-500 uppercase">{k}</div><div className="text-xs text-slate-800">{v}</div></div>)}</div>}
      {d.library_note && <div className="text-[11px] text-amber-700">{d.library_note}</div>}
    </div>
  );
}

export function SourceDialog({ source, onChange, onClose }) {
  const isDoc = !!source?.doc_id;
  const isRef = !!(source?.policy_id || source?.rule_id);
  return (
    <Dialog open={!!source} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="source-dialog" className="max-w-3xl bg-white border-slate-300">
        {source && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-display text-lg">
                {isRef ? <Scale className="w-4 h-4 text-emerald-600" /> : isDoc ? <FileText className="w-4 h-4 text-sky-600" /> : <Calculator className="w-4 h-4 text-violet-600" />}
                {isRef ? (source.policy_id ? "Bank lending policy" : "Regulatory rule") : isDoc ? `Source evidence - ${docLabel(source.doc_id)} p.${source.page}` : `Calculation trace - ${source.metric?.label}`}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">Highlighted text is the cited passage.</DialogDescription>
            </DialogHeader>
            {isRef ? <RefView source={source} /> : isDoc ? <DocView source={source} /> : source.metric ? <CalcView metric={source.metric} metrics={source.metrics} onChange={onChange} /> : <div className="text-sm text-slate-500">Metric not available yet.</div>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
